import type { Invoice, Workspace } from "./types";
export * from "./types";

export const STORAGE_KEY = "elyxir.workspace.v1";
export function uid(prefix = "item"): string {
  return `${prefix}_${globalThis.crypto?.randomUUID?.() ?? `${Date.now().toString(36)}_${Math.random().toString(36).slice(2)}`}`;
}

/** An entirely fictional workspace. Fresh demos use relative dates; persisted records never move. */
export function createWorkspace(referenceDate = new Date()): Workspace {
  if (!Number.isFinite(referenceDate.getTime()))
    throw new Error("The demo reference date must be valid.");
  const workspace: Workspace = {
    version: 1,
    settings: {
      name: "Alex Morgan",
      email: "alex@example.com",
      workspace: "Elyxir Studio",
      role: "owner",
      theme: "light",
      colorway: "lavender",
      accent: "indigo",
      customAccent: "#7e69ba",
      layout: "sidebar",
      density: "comfortable",
      locale: "en",
      direction: "ltr",
      notifications: true,
      currency: "USD",
      weekStartsMonday: true,
    },
    contacts: [
      {
        id: "contact_1",
        name: "Olivia Rhye",
        email: "olivia@example.com",
        company: "Layers",
        role: "Founder",
        status: "active",
        country: "United States",
        revenue: 18400,
        color: "#b8d1c0",
      },
      {
        id: "contact_2",
        name: "Phoenix Baker",
        email: "phoenix@example.com",
        company: "Sisyphus",
        role: "Product lead",
        status: "active",
        country: "Canada",
        revenue: 12600,
        color: "#cec5e8",
      },
      {
        id: "contact_3",
        name: "Lana Steiner",
        email: "lana@example.com",
        company: "Catalog",
        role: "Creative director",
        status: "active",
        country: "Germany",
        revenue: 9200,
        color: "#e8c4ac",
      },
      {
        id: "contact_4",
        name: "Demi Wilkinson",
        email: "demi@example.com",
        company: "Quotient",
        role: "Operations",
        status: "active",
        country: "United Kingdom",
        revenue: 7400,
        color: "#c2d6ed",
      },
      {
        id: "contact_5",
        name: "Drew Cano",
        email: "drew@example.com",
        company: "Hourglass",
        role: "CEO",
        status: "inactive",
        country: "Australia",
        revenue: 3600,
        color: "#e5c4d5",
      },
      {
        id: "contact_6",
        name: "Natali Craig",
        email: "natali@example.com",
        company: "Circooles",
        role: "Designer",
        status: "active",
        country: "France",
        revenue: 8900,
        color: "#d1d8b5",
      },
      {
        id: "contact_7",
        name: "Orlando Diggs",
        email: "orlando@example.com",
        company: "Command",
        role: "Engineering lead",
        status: "active",
        country: "United States",
        revenue: 21400,
        color: "#ddcbaa",
      },
      {
        id: "contact_8",
        name: "Andi Lane",
        email: "andi@example.com",
        company: "Goodwell",
        role: "Marketing director",
        status: "active",
        country: "Netherlands",
        revenue: 5600,
        color: "#becfe2",
      },
    ],
    deals: [
      {
        id: "deal_1",
        title: "Layers — Brand platform",
        contactId: "contact_1",
        value: 18500,
        stage: "proposal",
        probability: 75,
        closeDate: "2026-09-28",
      },
      {
        id: "deal_2",
        title: "Sisyphus — Customer portal",
        contactId: "contact_2",
        value: 24000,
        stage: "qualified",
        probability: 50,
        closeDate: "2026-10-08",
      },
      {
        id: "deal_3",
        title: "Catalog — Design subscription",
        contactId: "contact_3",
        value: 7200,
        stage: "won",
        probability: 100,
        closeDate: "2026-09-17",
      },
      {
        id: "deal_4",
        title: "Quotient — Analytics dashboard",
        contactId: "contact_4",
        value: 12600,
        stage: "lead",
        probability: 20,
        closeDate: "2026-10-12",
      },
      {
        id: "deal_5",
        title: "Circooles — Commerce redesign",
        contactId: "contact_6",
        value: 16400,
        stage: "proposal",
        probability: 70,
        closeDate: "2026-10-01",
      },
      {
        id: "deal_6",
        title: "Command — Developer experience",
        contactId: "contact_7",
        value: 32000,
        stage: "won",
        probability: 100,
        closeDate: "2026-09-12",
      },
      {
        id: "deal_7",
        title: "Goodwell — Landing pages",
        contactId: "contact_8",
        value: 4800,
        stage: "lead",
        probability: 25,
        closeDate: "2026-10-15",
      },
      {
        id: "deal_8",
        title: "Hourglass — Mobile refresh",
        contactId: "contact_5",
        value: 6500,
        stage: "lost",
        probability: 0,
        closeDate: "2026-09-09",
      },
    ],
    projects: [
      {
        id: "project_1",
        name: "Brand evolution",
        client: "Layers",
        description:
          "A thoughtful new visual identity and brand system for the next chapter.",
        color: "#e9d4ba",
        progress: 72,
        budget: 18500,
        status: "active",
        dueDate: "2026-09-28",
      },
      {
        id: "project_2",
        name: "Customer platform",
        client: "Sisyphus",
        description:
          "Bring accounts, billing, and customer insights into one considered experience.",
        color: "#cfcee8",
        progress: 48,
        budget: 24000,
        status: "active",
        dueDate: "2026-10-15",
      },
      {
        id: "project_3",
        name: "Commerce experience",
        client: "Circooles",
        description:
          "A faster, more accessible storefront built around the people who use it.",
        color: "#cfddbe",
        progress: 35,
        budget: 16400,
        status: "active",
        dueDate: "2026-10-08",
      },
      {
        id: "project_4",
        name: "Developer workspace",
        client: "Command",
        description:
          "An intuitive workspace for collaborating on software releases.",
        color: "#bfd9e2",
        progress: 100,
        budget: 32000,
        status: "completed",
        dueDate: "2026-09-12",
      },
    ],
    tasks: [
      {
        id: "task_1",
        title: "Explore homepage directions",
        description: "Prepare three art directions with responsive examples.",
        projectId: "project_1",
        status: "progress",
        priority: "high",
        dueDate: "2026-09-22",
        assignee: "Alex Morgan",
      },
      {
        id: "task_2",
        title: "Audit component accessibility",
        description: "Check focus order, contrast, and form labels.",
        projectId: "project_2",
        status: "todo",
        priority: "high",
        dueDate: "2026-09-24",
        assignee: "Olivia Rhye",
      },
      {
        id: "task_3",
        title: "Review checkout flow",
        description: "Review shipping, payment, and confirmation states.",
        projectId: "project_3",
        status: "review",
        priority: "medium",
        dueDate: "2026-09-21",
        assignee: "Phoenix Baker",
      },
      {
        id: "task_4",
        title: "Finalize color tokens",
        description: "Document semantic tokens for light and dark themes.",
        projectId: "project_1",
        status: "done",
        priority: "medium",
        dueDate: "2026-09-18",
        assignee: "Lana Steiner",
      },
      {
        id: "task_5",
        title: "Build account settings",
        description:
          "Complete profile, preferences, and notification controls.",
        projectId: "project_2",
        status: "progress",
        priority: "high",
        dueDate: "2026-09-25",
        assignee: "Alex Morgan",
      },
      {
        id: "task_6",
        title: "Prepare stakeholder update",
        description: "Gather this week’s milestones and open questions.",
        projectId: "project_1",
        status: "todo",
        priority: "low",
        dueDate: "2026-09-23",
        assignee: "Demi Wilkinson",
      },
      {
        id: "task_7",
        title: "Optimize product thumbnails",
        description: "Create appropriately sized images for each breakpoint.",
        projectId: "project_3",
        status: "todo",
        priority: "medium",
        dueDate: "2026-09-26",
        assignee: "Lana Steiner",
      },
      {
        id: "task_8",
        title: "Publish developer documentation",
        description: "Complete installation and customization guides.",
        projectId: "project_4",
        status: "done",
        priority: "high",
        dueDate: "2026-09-12",
        assignee: "Phoenix Baker",
      },
    ],
    products: [
      {
        id: "product_1",
        name: "Studio Headphones",
        sku: "ELX-AU-001",
        category: "Audio",
        price: 189,
        stock: 42,
        status: "active",
        color: "#d6dfcf",
      },
      {
        id: "product_2",
        name: "Everyday Backpack",
        sku: "ELX-AC-002",
        category: "Accessories",
        price: 96,
        stock: 68,
        status: "active",
        color: "#e8d9c8",
      },
      {
        id: "product_3",
        name: "Arc Desk Lamp",
        sku: "ELX-HO-003",
        category: "Home office",
        price: 128,
        stock: 17,
        status: "active",
        color: "#d7d4e4",
      },
      {
        id: "product_4",
        name: "Mechanical Keyboard",
        sku: "ELX-TE-004",
        category: "Technology",
        price: 149,
        stock: 31,
        status: "active",
        color: "#d0dce5",
      },
      {
        id: "product_5",
        name: "Essential Notebook",
        sku: "ELX-ST-005",
        category: "Stationery",
        price: 24,
        stock: 124,
        status: "active",
        color: "#e8cfbf",
      },
      {
        id: "product_6",
        name: "Ceramic Travel Cup",
        sku: "ELX-LI-006",
        category: "Lifestyle",
        price: 38,
        stock: 8,
        status: "active",
        color: "#d7dfbe",
      },
      {
        id: "product_7",
        name: "Wireless Charging Pad",
        sku: "ELX-TE-007",
        category: "Technology",
        price: 59,
        stock: 0,
        status: "draft",
        color: "#dbd1e4",
      },
      {
        id: "product_8",
        name: "Canvas Organizer",
        sku: "ELX-AC-008",
        category: "Accessories",
        price: 32,
        stock: 23,
        status: "archived",
        color: "#e4d6c6",
      },
    ],
    orders: [
      {
        id: "ORD-2408",
        customer: "Olivia Rhye",
        email: "olivia@example.com",
        date: "2026-09-20",
        total: 285,
        items: 2,
        status: "processing",
      },
      {
        id: "ORD-2407",
        customer: "Phoenix Baker",
        email: "phoenix@example.com",
        date: "2026-09-20",
        total: 149,
        items: 1,
        status: "pending",
      },
      {
        id: "ORD-2406",
        customer: "Lana Steiner",
        email: "lana@example.com",
        date: "2026-09-19",
        total: 190,
        items: 3,
        status: "shipped",
      },
      {
        id: "ORD-2405",
        customer: "Demi Wilkinson",
        email: "demi@example.com",
        date: "2026-09-19",
        total: 378,
        items: 2,
        status: "delivered",
      },
      {
        id: "ORD-2404",
        customer: "Natali Craig",
        email: "natali@example.com",
        date: "2026-09-18",
        total: 96,
        items: 1,
        status: "delivered",
      },
      {
        id: "ORD-2403",
        customer: "Orlando Diggs",
        email: "orlando@example.com",
        date: "2026-09-18",
        total: 277,
        items: 2,
        status: "shipped",
      },
      {
        id: "ORD-2402",
        customer: "Andi Lane",
        email: "andi@example.com",
        date: "2026-09-17",
        total: 62,
        items: 2,
        status: "cancelled",
      },
      {
        id: "ORD-2401",
        customer: "Drew Cano",
        email: "drew@example.com",
        date: "2026-09-16",
        total: 128,
        items: 1,
        status: "delivered",
      },
    ],
    invoices: [
      {
        id: "invoice_1",
        number: "INV-2026-042",
        customer: "Layers",
        email: "billing@example.com",
        date: "2026-09-15",
        dueDate: "2026-09-30",
        status: "sent",
        lines: [
          {
            description: "Brand strategy workshop",
            quantity: 1,
            unitPrice: 2400,
          },
          {
            description: "Visual identity design",
            quantity: 1,
            unitPrice: 6800,
          },
        ],
        tax: 10,
        notes: "Thank you for building something great with us.",
      },
      {
        id: "invoice_2",
        number: "INV-2026-041",
        customer: "Command",
        email: "command@example.com",
        date: "2026-09-12",
        dueDate: "2026-09-26",
        status: "paid",
        lines: [
          {
            description: "Developer workspace — final milestone",
            quantity: 1,
            unitPrice: 16000,
          },
        ],
        tax: 0,
        notes: "Final project milestone.",
      },
      {
        id: "invoice_3",
        number: "INV-2026-040",
        customer: "Catalog",
        email: "catalog@example.com",
        date: "2026-09-01",
        dueDate: "2026-09-15",
        status: "overdue",
        lines: [
          {
            description: "Monthly design subscription",
            quantity: 1,
            unitPrice: 3600,
          },
        ],
        tax: 10,
        notes: "September design support.",
      },
      {
        id: "invoice_4",
        number: "INV-2026-043",
        customer: "Circooles",
        email: "circooles@example.com",
        date: "2026-09-20",
        dueDate: "2026-10-04",
        status: "draft",
        lines: [
          {
            description: "Commerce discovery sprint",
            quantity: 2,
            unitPrice: 1800,
          },
        ],
        tax: 0,
        notes: "",
      },
    ],
    events: [
      {
        id: "event_1",
        title: "Weekly studio stand-up",
        date: "2026-09-21",
        time: "09:30",
        duration: 30,
        type: "meeting",
        description: "Priorities, progress, and a little inspiration.",
      },
      {
        id: "event_2",
        title: "Layers creative review",
        date: "2026-09-22",
        time: "14:00",
        duration: 60,
        type: "meeting",
        description: "Walk through homepage directions with the client.",
      },
      {
        id: "event_3",
        title: "Checkout review due",
        date: "2026-09-21",
        time: "17:00",
        duration: 30,
        type: "deadline",
        description: "Share reviewed screens and feedback.",
      },
      {
        id: "event_4",
        title: "Deep work",
        date: "2026-09-23",
        time: "10:00",
        duration: 120,
        type: "personal",
        description: "A quiet block for focused design work.",
      },
      {
        id: "event_5",
        title: "Customer platform demo",
        date: "2026-09-24",
        time: "15:00",
        duration: 45,
        type: "meeting",
        description: "Review the new account experience.",
      },
      {
        id: "event_6",
        title: "September retrospective",
        date: "2026-09-25",
        time: "16:00",
        duration: 60,
        type: "meeting",
        description: "What worked, what we learned, what comes next.",
      },
    ],
    messages: [
      {
        id: "message_1",
        sender: "Olivia Rhye",
        email: "olivia@example.com",
        subject: "A few thoughts on the new direction",
        body: "Hi Alex,\n\nThe team loved the initial concepts. The softer palette feels exactly right for our next chapter. Could we explore the second direction in our review on Tuesday?\n\nThanks for all the care you are putting into this.\nOlivia",
        date: "2026-09-20T10:42:00Z",
        read: false,
        starred: true,
        folder: "inbox",
      },
      {
        id: "message_2",
        sender: "Phoenix Baker",
        email: "phoenix@example.com",
        subject: "Customer portal — sprint notes",
        body: "Hey team,\n\nThe account settings screens are ready for review. I have documented the loading, empty, and error states alongside the main flows.\n\nLet me know what you think.\nPhoenix",
        date: "2026-09-20T09:15:00Z",
        read: false,
        starred: false,
        folder: "inbox",
      },
      {
        id: "message_3",
        sender: "Lana Steiner",
        email: "lana@example.com",
        subject: "Your weekly dose of inspiration",
        body: "A small collection of references for our next design session: editorial typography, quiet color, and delightful little interactions. Looking forward to discussing these together.",
        date: "2026-09-19T16:30:00Z",
        read: true,
        starred: false,
        folder: "inbox",
      },
      {
        id: "message_4",
        sender: "Demi Wilkinson",
        email: "demi@example.com",
        subject: "September project check-in",
        body: "Hi Alex,\n\nCould you share the latest timeline for the analytics dashboard? We are aligning the launch with our upcoming team meeting.\n\nBest,\nDemi",
        date: "2026-09-19T14:05:00Z",
        read: true,
        starred: true,
        folder: "inbox",
      },
      {
        id: "message_5",
        sender: "Alex Morgan",
        email: "alex@example.com",
        subject: "Re: Developer workspace handoff",
        body: "The final handoff is ready. It includes design tokens, component documentation, and the implementation notes we discussed. Thank you for a wonderful collaboration.",
        date: "2026-09-18T11:00:00Z",
        read: true,
        starred: false,
        folder: "sent",
      },
      {
        id: "message_6",
        sender: "Alex Morgan",
        email: "alex@example.com",
        subject: "October planning",
        body: "Draft agenda for our October planning session.",
        date: "2026-09-20T08:00:00Z",
        read: true,
        starred: false,
        folder: "drafts",
      },
      {
        id: "message_7",
        sender: "Natali Craig",
        email: "natali@example.com",
        subject: "Thanks for the workshop",
        body: "We left the session with a much clearer idea of what matters for our customers. Excited to see it take shape.",
        date: "2026-09-14T12:20:00Z",
        read: true,
        starred: false,
        folder: "archive",
      },
    ],
    files: [
      {
        id: "file_1",
        name: "Brand-guidelines.md",
        type: "text/markdown",
        size: 188,
        folder: "Brand assets",
        date: "2026-09-18",
        starred: true,
        content:
          "# Elyxir Studio\n\nUse a clear hierarchy, generous whitespace, and accessible color contrast. Demo brand guidelines for the fictional Elyxir Studio workspace.",
      },
      {
        id: "file_2",
        name: "Project-brief.txt",
        type: "text/plain",
        size: 125,
        folder: "Projects",
        date: "2026-09-19",
        starred: true,
        content:
          "Customer platform brief\nBuild a thoughtful account experience with clear settings, readable tables, and responsive navigation.",
      },
      {
        id: "file_3",
        name: "September-notes.md",
        type: "text/markdown",
        size: 110,
        folder: "Documents",
        date: "2026-09-20",
        starred: false,
        content:
          "# September notes\n\n- Finish the brand exploration\n- Review customer settings\n- Prepare the October project plan",
      },
      {
        id: "file_4",
        name: "Design-tokens.json",
        type: "application/json",
        size: 78,
        folder: "Brand assets",
        date: "2026-09-16",
        starred: false,
        content:
          '{"brand":"Elyxir","accent":"#c7f36b","radius":16,"spacing":8}',
      },
      {
        id: "file_5",
        name: "Handoff-checklist.txt",
        type: "text/plain",
        size: 99,
        folder: "Projects",
        date: "2026-09-12",
        starred: false,
        content:
          "Handoff checklist\n1. Source files\n2. Component documentation\n3. Setup guide\n4. Accessibility review",
      },
    ],
    members: [
      {
        id: "member_1",
        name: "Alex Morgan",
        email: "alex@example.com",
        role: "owner",
        status: "active",
        color: "#c4d6b9",
      },
      {
        id: "member_2",
        name: "Olivia Rhye",
        email: "olivia@example.com",
        role: "admin",
        status: "active",
        color: "#b8d1c0",
      },
      {
        id: "member_3",
        name: "Phoenix Baker",
        email: "phoenix@example.com",
        role: "member",
        status: "active",
        color: "#cec5e8",
      },
      {
        id: "member_4",
        name: "Lana Steiner",
        email: "lana@example.com",
        role: "member",
        status: "active",
        color: "#e8c4ac",
      },
      {
        id: "member_5",
        name: "Demi Wilkinson",
        email: "demi@example.com",
        role: "viewer",
        status: "invited",
        color: "#c2d6ed",
      },
    ],
    activity: [
      {
        id: "activity_1",
        title: "New order received",
        description: "Olivia placed order ORD-2408 for $285.",
        time: "2026-09-20T10:45:00Z",
        kind: "sale",
      },
      {
        id: "activity_2",
        title: "A new chapter for Layers",
        description: "Brand evolution moved to 72% complete.",
        time: "2026-09-20T09:30:00Z",
        kind: "project",
      },
      {
        id: "activity_3",
        title: "Welcome to the team",
        description: "Demi was invited to the workspace.",
        time: "2026-09-19T16:15:00Z",
        kind: "team",
      },
      {
        id: "activity_4",
        title: "Invoice paid",
        description: "Command completed the final project payment.",
        time: "2026-09-19T13:00:00Z",
        kind: "sale",
      },
      {
        id: "activity_5",
        title: "Workspace is up to date",
        description: "Your local changes were saved.",
        time: "2026-09-19T10:00:00Z",
        kind: "system",
      },
    ],
  };
  // Keep the seed's relative schedule consistent across month/year boundaries and DST.
  const year = referenceDate.getFullYear();
  const month = referenceDate.getMonth();
  const referenceNoon = Date.UTC(year, month, referenceDate.getDate(), 12);
  const offset = referenceNoon - Date.UTC(2026, 8, 20, 12);
  const shiftDate = (source: string) =>
    new Date(Date.parse(`${source}T12:00:00Z`) + offset)
      .toISOString()
      .slice(0, 10);
  const shiftTimestamp = (source: string) =>
    new Date(Date.parse(source) + offset).toISOString();
  workspace.deals.forEach((record) => {
    record.closeDate = shiftDate(record.closeDate);
  });
  [...workspace.projects, ...workspace.tasks].forEach((record) => {
    record.dueDate = shiftDate(record.dueDate);
  });
  [...workspace.orders, ...workspace.events, ...workspace.files].forEach(
    (record) => {
      record.date = shiftDate(record.date);
    },
  );
  workspace.invoices.forEach((record) => {
    record.date = shiftDate(record.date);
    record.dueDate = shiftDate(record.dueDate);
    record.number = record.number.replace("2026", record.date.slice(0, 4));
  });
  workspace.messages.forEach((record) => {
    record.date = shiftTimestamp(record.date);
  });
  workspace.activity.forEach((record) => {
    record.time = shiftTimestamp(record.time);
  });

  // Real fictional order records power every chart and table; there are no separate metric arrays.
  for (let previous = 5; previous >= 1; previous--) {
    const index = 5 - previous;
    const firstContact = workspace.contacts[index];
    const secondContact = workspace.contacts[index + 2];
    const firstQuantity = index + 2;
    const secondQuantity = index + 1;
    workspace.orders.push(
      {
        id: `ORD-${2300 + index * 2}`,
        customer: firstContact.name,
        email: firstContact.email,
        date: new Date(Date.UTC(year, month - previous, 8, 12))
          .toISOString()
          .slice(0, 10),
        total:
          workspace.products[0].price * firstQuantity +
          workspace.products[1].price * secondQuantity,
        items: firstQuantity + secondQuantity,
        status: "delivered",
      },
      {
        id: `ORD-${2301 + index * 2}`,
        customer: secondContact.name,
        email: secondContact.email,
        date: new Date(Date.UTC(year, month - previous, 19, 12))
          .toISOString()
          .slice(0, 10),
        total:
          workspace.products[2].price * firstQuantity +
          workspace.products[5].price * (index + 3),
        items: firstQuantity + index + 3,
        status: "delivered",
      },
    );
  }
  const monthName = referenceDate.toLocaleDateString("en-US", {
    month: "long",
  });
  const nextMonthName = new Date(year, month + 1, 1).toLocaleDateString(
    "en-US",
    { month: "long" },
  );
  const reviewDay = new Date(
    `${workspace.events[1].date}T12:00:00`,
  ).toLocaleDateString("en-US", { weekday: "long" });
  workspace.events[5].title = `${monthName} retrospective`;
  workspace.messages[0].body = workspace.messages[0].body.replace(
    "Tuesday",
    reviewDay,
  );
  workspace.messages[3].subject = `${monthName} project check-in`;
  workspace.messages[5].subject = `${nextMonthName} planning`;
  workspace.messages[5].body = `Draft agenda for our ${nextMonthName} planning session.`;
  workspace.invoices[2].notes = `${monthName} design support.`;
  workspace.files[2].name = `${monthName}-notes.md`;
  workspace.files[2].content = workspace.files[2]
    .content!.replace("September", monthName)
    .replace("October", nextMonthName);
  // Download metadata reflects the actual UTF-8 content bundled with each text file.
  workspace.files.forEach((file) => {
    if (file.content !== undefined)
      file.size = new TextEncoder().encode(file.content).length;
  });
  return workspace;
}

type Check = (value: unknown, path: string) => void;
function invalid(path: string, reason: string): never {
  throw new Error(`Invalid workspace: ${path} ${reason}.`);
}
const str =
  (max = 500, min = 0): Check =>
  (v, p) => {
    if (typeof v !== "string" || v.length > max || v.length < min)
      invalid(p, `must be text with ${min}–${max} characters`);
  };
const num =
  (min = 0, max = 1_000_000_000, integer = false): Check =>
  (v, p) => {
    if (
      typeof v !== "number" ||
      !Number.isFinite(v) ||
      v < min ||
      v > max ||
      (integer && !Number.isInteger(v))
    )
      invalid(
        p,
        `must be ${integer ? "an integer" : "a number"} between ${min} and ${max}`,
      );
  };
const oneOf =
  (...values: unknown[]): Check =>
  (v, p) => {
    if (!values.includes(v)) invalid(p, "has an unsupported value");
  };
const bool: Check = (v, p) => {
  if (typeof v !== "boolean") invalid(p, "must be a boolean");
};
const date: Check = (v, p) => {
  if (
    typeof v !== "string" ||
    !/^\d{4}-\d{2}-\d{2}$/.test(v) ||
    !Number.isFinite(Date.parse(v)) ||
    new Date(v).toISOString().slice(0, 10) !== v
  )
    invalid(p, "must be a valid YYYY-MM-DD date");
};
const instant: Check = (v, p) => {
  if (
    typeof v !== "string" ||
    v.length > 40 ||
    !/^\d{4}-\d{2}-\d{2}T/.test(v) ||
    !Number.isFinite(Date.parse(v))
  )
    invalid(p, "must be an ISO timestamp");
};
const time: Check = (v, p) => {
  if (typeof v !== "string" || !/^([01]\d|2[0-3]):[0-5]\d$/.test(v))
    invalid(p, "must be a valid HH:MM time");
};
const email: Check = (v, p) => {
  str(254, 3)(v, p);
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v as string))
    invalid(p, "must be a valid email address");
};
const color: Check = (v, p) => {
  if (typeof v !== "string" || !/^#[0-9a-f]{6}$/i.test(v))
    invalid(p, "must be a six-digit hex color");
};
const optional =
  (check: Check): Check =>
  (v, p) => {
    if (v !== undefined) check(v, p);
  };
const object =
  (fields: Record<string, Check>): Check =>
  (v, p) => {
    if (
      !v ||
      typeof v !== "object" ||
      Array.isArray(v) ||
      ![Object.prototype, null].includes(Object.getPrototypeOf(v))
    )
      invalid(p, "must be an object");
    for (const key of Object.keys(v as object))
      if (!Object.hasOwn(fields, key))
        invalid(`${p}.${key}`, "is not a supported field");
    for (const [key, check] of Object.entries(fields))
      check((v as Record<string, unknown>)[key], `${p}.${key}`);
  };
const array =
  (check: Check, max = 5000, min = 0): Check =>
  (v, p) => {
    if (!Array.isArray(v) || v.length > max || v.length < min)
      invalid(p, `must contain ${min}–${max} entries`);
    (v as unknown[]).forEach((entry, i) => check(entry, `${p}[${i}]`));
    const ids = (v as { id?: string }[])
      .map((e) => e?.id)
      .filter((id) => id !== undefined);
    if (ids.length !== new Set(ids).size) invalid(p, "contains duplicate IDs");
  };
const id = str(100, 1);
const text = str();
const amount = num();
const schema = object({
  version: oneOf(1),
  contacts: array(
    object({
      id,
      name: str(200, 1),
      email,
      company: text,
      role: text,
      status: oneOf("active", "inactive"),
      country: text,
      revenue: amount,
      color,
    }),
  ),
  deals: array(
    object({
      id,
      title: str(500, 1),
      contactId: str(100),
      value: amount,
      stage: oneOf("lead", "qualified", "proposal", "won", "lost"),
      probability: num(0, 100),
      closeDate: date,
    }),
  ),
  tasks: array(
    object({
      id,
      title: str(500, 1),
      description: str(20000),
      projectId: str(100),
      status: oneOf("todo", "progress", "review", "done"),
      priority: oneOf("low", "medium", "high"),
      dueDate: date,
      assignee: text,
    }),
  ),
  projects: array(
    object({
      id,
      name: str(200, 1),
      client: text,
      description: str(20000),
      color,
      progress: num(0, 100),
      budget: amount,
      status: oneOf("active", "paused", "completed"),
      dueDate: date,
    }),
  ),
  products: array(
    object({
      id,
      name: str(200, 1),
      sku: str(100),
      category: text,
      price: amount,
      stock: num(0, 1_000_000, true),
      status: oneOf("active", "draft", "archived"),
      color,
    }),
  ),
  orders: array(
    object({
      id,
      customer: str(200, 1),
      email,
      date,
      total: amount,
      items: num(1, 1_000_000, true),
      status: oneOf(
        "pending",
        "processing",
        "shipped",
        "delivered",
        "cancelled",
      ),
    }),
  ),
  invoices: array(
    object({
      id,
      number: str(100, 1),
      customer: str(200, 1),
      email,
      date,
      dueDate: date,
      status: oneOf("draft", "sent", "paid", "overdue"),
      lines: array(
        object({
          description: str(1000, 1),
          quantity: num(0.001, 1_000_000),
          unitPrice: amount,
        }),
        200,
        1,
      ),
      tax: num(0, 100),
      notes: str(20000),
    }),
  ),
  events: array(
    object({
      id,
      title: str(500, 1),
      date,
      time,
      duration: num(1, 10080, true),
      type: oneOf("meeting", "deadline", "personal"),
      description: str(20000),
    }),
  ),
  messages: array(
    object({
      id,
      sender: str(200, 1),
      email,
      subject: str(1000),
      body: str(100000),
      date: instant,
      read: bool,
      starred: bool,
      folder: oneOf("inbox", "sent", "drafts", "archive"),
    }),
  ),
  files: array(
    object({
      id,
      name: str(255, 1),
      type: str(200),
      size: num(0, 20_000_000, true),
      folder: str(255),
      date,
      starred: bool,
      content: optional(str(1_000_000)),
    }),
    500,
  ),
  members: array(
    object({
      id,
      name: str(200, 1),
      email,
      role: oneOf("owner", "admin", "member", "viewer"),
      status: oneOf("active", "invited"),
      color,
    }),
    500,
  ),
  activity: array(
    object({
      id,
      title: str(500, 1),
      description: str(20000),
      time: instant,
      kind: oneOf("sale", "project", "team", "system"),
    }),
  ),
  settings: object({
    name: str(200, 1),
    email,
    workspace: str(200, 1),
    role: str(100),
    theme: oneOf("light", "dark", "system"),
    colorway: optional(
      oneOf(
        "pearl",
        "lavender",
        "sand",
        "graphite",
        "sage",
        "blush",
        "ocean",
        "clay",
      ),
    ),
    accent: oneOf(
      "blue",
      "lime",
      "emerald",
      "indigo",
      "rose",
      "amber",
      "custom",
    ),
    customAccent: optional(color),
    layout: oneOf(
      "sidebar",
      "compact",
      "horizontal",
      "dual",
      "floating",
      "minimal",
      "wide",
      "boxed",
      "rail",
      "dark-sidebar",
    ),
    density: oneOf("comfortable", "compact"),
    locale: oneOf("en", "fr", "ar"),
    direction: oneOf("ltr", "rtl"),
    notifications: bool,
    currency: oneOf("USD", "EUR", "GBP"),
    weekStartsMonday: bool,
  }),
});

export function validateWorkspace(value: unknown): Workspace {
  schema(value, "root");
  const serialized = JSON.stringify(value);
  if (serialized.length > 5_000_000)
    invalid("root", "exceeds the 5 MB workspace limit");
  return value as Workspace;
}

export function loadWorkspace(): Workspace {
  if (typeof localStorage === "undefined") return createWorkspace();
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return createWorkspace();
  try {
    return validateWorkspace(JSON.parse(raw));
  } catch (error) {
    throw new Error(
      `Your saved workspace could not be loaded. Export or restore your backup before resetting. ${error instanceof Error ? error.message : ""}`,
    );
  }
}

export function saveWorkspace(state: Workspace): void {
  validateWorkspace(state);
  if (typeof localStorage === "undefined")
    throw new Error(
      "Browser storage is unavailable. Your changes were not saved.",
    );
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    throw new Error(
      "Your changes could not be saved. Browser storage may be full or disabled. Export a backup before leaving.",
    );
  }
}

export function toCSV(headers: string[], rows: unknown[][]): string {
  const cell = (value: unknown) => {
    let result = value === null || value === undefined ? "" : String(value);
    // Spreadsheet software may interpret attacker-controlled cells as formulas.
    if (
      typeof value === "string" &&
      (/^[\s\u0000-\u001f]*[=+@-]/.test(result) || /^[\t\r\n]/.test(result))
    )
      result = `'${result}`;
    return `"${result.replaceAll('"', '""')}"`;
  };
  return [headers, ...rows].map((row) => row.map(cell).join(",")).join("\r\n");
}

export function downloadFile(
  name: string,
  content: string | Blob,
  type = "text/plain;charset=utf-8",
): void {
  const blob =
    content instanceof Blob ? content : new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = name.replace(/[\\/\u0000-\u001f]/g, "_");
  document.body.append(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function formatMoney(value: number, currency = "USD"): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    maximumFractionDigits: 2,
  }).format(value);
}
export function formatDate(value: string): string {
  const parsed = new Date(
    /^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T12:00:00` : value,
  );
  return Number.isFinite(parsed.getTime())
    ? new Intl.DateTimeFormat("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      }).format(parsed)
    : "—";
}
export function invoiceTotal(invoice: Invoice): number {
  const subtotal = invoice.lines.reduce(
    (sum, line) => sum + Math.round(line.quantity * line.unitPrice * 100),
    0,
  );
  return Math.round(subtotal * (1 + invoice.tax / 100)) / 100;
}
export function exportWorkspace(state: Workspace): string {
  return JSON.stringify(validateWorkspace(state), null, 2);
}
export function importWorkspace(content: string): Workspace {
  if (content.length > 5_000_000)
    throw new Error("The backup exceeds the 5 MB workspace limit.");
  return validateWorkspace(JSON.parse(content));
}
